import { useState } from "react";
import { updateRoute } from "../../services";
import { RouteManagementRow } from "../../types/route.type";
import ConfirmModal from "../ConfirmModal";
import { QR_OFF_WITH_TEST_MODE_NOTE } from "./routeText";

// What each mode does to the bills the route issues from now on.
const GO_LIVE_DESCRIPTION =
  "บิลที่ออกจากสายนี้หลังจากนี้เป็นบิลจริง: เลขบิลไม่มี T นำหน้า และส่ง e-Tax ตามปกติ";
const BACK_TO_TEST_DESCRIPTION =
  "บิลที่ออกจากสายนี้หลังจากนี้เลขบิลขึ้นต้นด้วย T และไม่ถูกส่ง e-Tax";

interface TestModeDialogProps {
  /** The route being switched; null keeps the dialog closed. */
  target: RouteManagementRow | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function TestModeDialog({
  target,
  onClose,
  onSaved,
}: TestModeDialogProps) {
  const [saving, setSaving] = useState<boolean>(false);

  const isGoingLive = Boolean(target?.is_test_mode);
  const title = isGoingLive
    ? `ปิดโหมดทดสอบ สาย ${target?.route_code ?? ""}`
    : `เปิดโหมดทดสอบ สาย ${target?.route_code ?? ""}`;
  const isSwitchingQrOff = !isGoingLive && Boolean(target?.qr_payment_enabled);
  const backToTestDescription = isSwitchingQrOff
    ? `${BACK_TO_TEST_DESCRIPTION} ${QR_OFF_WITH_TEST_MODE_NOTE}`
    : BACK_TO_TEST_DESCRIPTION;
  const successMsg = isGoingLive
    ? "เปิดใช้งานจริงสำเร็จ"
    : "เปลี่ยนเป็นโหมดทดสอบสำเร็จ";

  /** Answers whether the API stored the new mode. */
  const saveMode = async (route: RouteManagementRow): Promise<boolean> => {
    try {
      await updateRoute(
        route.uuid,
        { is_test_mode: !route.is_test_mode },
        successMsg,
      );
      return true;
    } catch {
      // The interceptor already showed the API's reason; the route keeps its mode.
      return false;
    }
  };

  const handleConfirm = async () => {
    if (!target) return;
    setSaving(true);
    const saved = await saveMode(target);
    setSaving(false);
    if (saved) onSaved();
    onClose();
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  return (
    <ConfirmModal
      open={Boolean(target)}
      title={title}
      description={isGoingLive ? GO_LIVE_DESCRIPTION : backToTestDescription}
      confirmLabel={isGoingLive ? "เปิดใช้งานจริง" : "เปลี่ยนเป็นโหมดทดสอบ"}
      confirmColor="warning"
      loading={saving}
      onClose={handleClose}
      onConfirm={handleConfirm}
    />
  );
}
